import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Globe, Monitor, Smartphone, Tablet, Plus, CheckCircle } from 'lucide-react';

/**
 * Modal for selecting assessment variants
 * Shows when assessment has many variants (>3) for better UX
 */
const VariantSelectorModal = ({ isOpen, onClose, assessment, onSelectVariant, onCreateVariant }) => {
  if (!isOpen || !assessment) return null;

  const isPrimary = assessment.variants && assessment.variants.length > 0;
  const variants = assessment.variants || [];

  const getDeviceIcon = (deviceType) => {
    const icons = {
      phone: Smartphone,
      tablet: Tablet,
      desktop: Monitor,
      other: Globe
    };
    return icons[deviceType] || Globe;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'published':
        return 'border-emerald-200 bg-emerald-50';
      case 'review':
        return 'border-blue-200 bg-blue-50';
      case 'draft':
        return 'border-amber-200 bg-amber-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 dark:bg-opacity-70">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl dark:shadow-gray-900/20 max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Select Version</h2>
              <p className="text-sm text-gray-500 mt-1">
                {assessment.name || assessment.survey?.name || 'Untitled Assessment'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Primary Version */}
              {isPrimary && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    onSelectVariant(assessment);
                    onClose();
                  }}
                  className="relative p-4 border-2 border-primary-500 rounded-xl hover:shadow-lg transition-all text-left bg-primary-50"
                >
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 text-xs font-semibold rounded bg-primary-100 text-primary-700 border border-primary-300">
                      PRIMARY
                    </span>
                  </div>
                  
                  <div className="mt-6 mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="w-4 h-4 text-primary-600" />
                      <span className="text-sm font-medium text-gray-700">
                        {assessment.language || 'en-US'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-primary-600" />
                      <span className="text-sm text-gray-600">Web Desktop (Primary)</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-primary-200">
                    <span className="text-xs font-medium text-primary-700">v{assessment.version || '1.0'}</span>
                    <CheckCircle className="w-4 h-4 text-primary-600" />
                  </div>
                </motion.button>
              )}

              {/* Variant Versions */}
              {variants.map((variant) => {
                const DeviceIcon = getDeviceIcon(variant.device_type);
                const statusColor = getStatusColor(variant.publication_status);
                
                return (
                  <motion.button
                    key={variant.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      onSelectVariant(variant);
                      onClose();
                    }}
                    className={`relative p-4 border-2 rounded-xl hover:shadow-lg transition-all text-left ${statusColor}`}
                  >
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-white dark:bg-gray-800 bg-opacity-60 text-gray-700 border border-gray-300">
                        VARIANT
                      </span>
                    </div>
                    
                    <div className="mt-6 mb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">
                          {variant.language}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DeviceIcon className="w-4 h-4 text-gray-600" />
                        <span className="text-sm text-gray-600">{variant.device_name}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-gray-300">
                      <span className="text-xs font-medium text-gray-600">v{variant.version || '1.0'}</span>
                      <span className="text-xs text-gray-500 capitalize">{variant.publication_status}</span>
                    </div>
                  </motion.button>
                );
              })}

              {/* Create New Variant */}
              {isPrimary && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    onCreateVariant(assessment);
                    onClose();
                  }}
                  className="p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all flex flex-col items-center justify-center gap-3 min-h-[140px]"
                >
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <Plus className="w-6 h-6 text-primary-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">Create New Variant</span>
                  <span className="text-xs text-gray-500">Add language or device version</span>
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default VariantSelectorModal;
